/** Auth0 implementation — a 1:1 wrapper over @auth0/auth0-react so the
 *  default provider's behavior is byte-equivalent to the pre-abstraction app.
 *  The DEV_AUTH_MOCK module alias (next.config.js) intercepts the package
 *  import below, so dev personas keep working with zero changes. */
import React, { ReactNode, useCallback, useMemo } from "react";
import Router from "next/router";
import {
  Auth0Provider,
  useAuth0,
  withAuthenticationRequired,
} from "@auth0/auth0-react";

import {
  auth0LoginArgs,
  auth0TokenArgs,
  mapUser,
  normalizeReturnTo,
  resolveAuthScope,
} from "../mapping";
import type { AuthHook, GetTokenOptions, LoginOptions } from "../types";

const audience = process.env.NEXT_PUBLIC_AUDIENCE;
// AUTH0_SCOPE is server-only (not NEXT_PUBLIC_) so it is undefined in the
// browser bundle; resolveAuthScope supplies the offline_access default.
const scope = resolveAuthScope(
  process.env.NEXT_PUBLIC_AUTH0_SCOPE || process.env.AUTH0_SCOPE,
);

const onRedirectCallback = (appState?: { returnTo?: string }) => {
  Router.replace(normalizeReturnTo(appState?.returnTo));
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const redirectUri =
    process.env.NEXT_PUBLIC_APP_BASE_URL ||
    process.env.NEXT_PUBLIC_BASE_URL ||
    process.env.APP_BASE_URL;
  if (!redirectUri) {
    throw new Error(
      "Auth0 redirect URI is not configured. Set NEXT_PUBLIC_APP_BASE_URL, NEXT_PUBLIC_BASE_URL, or APP_BASE_URL.",
    );
  }
  const domain = process.env.NEXT_PUBLIC_AUTH0_DOMAIN;
  if (!domain) {
    throw new Error(
      "Auth0 domain is not configured. Please set NEXT_PUBLIC_AUTH0_DOMAIN or NEXT_PUBLIC_DOMAIN.",
    );
  }
  return (
    <Auth0Provider
      domain={domain}
      clientId={process.env.NEXT_PUBLIC_CLIENT_ID}
      // Honor appState.returnTo via the Next router (invite flow); "/" default.
      onRedirectCallback={onRedirectCallback}
      // Rotating refresh tokens + persisted cache: silent renewal works
      // without the third-party-cookie iframe (blocked by Safari ITP and
      // Chrome's cookie phase-out), and sessions survive full page loads —
      // without this every reload bounced through the Auth0 login page.
      // Rotation is enabled on the Auth0 SPA client; localStorage exposure
      // is bounded by the 1h access-token TTL and single-use refresh tokens.
      useRefreshTokens
      cacheLocation="localstorage"
      authorizationParams={{
        audience: process.env.NEXT_PUBLIC_AUDIENCE,
        // Same scope as token renewal (incl. offline_access): the initial
        // login must mint the refresh token, and matching scopes keep the
        // SDK cache key aligned so the first getAccessToken() hits cache.
        scope,
        redirect_uri: redirectUri,
      }}
    >
      {children}
    </Auth0Provider>
  );
}

export function useAuth(): AuthHook {
  const {
    isLoading,
    isAuthenticated,
    error,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const user = useMemo(() => mapUser(auth0User), [auth0User]);

  const login = useCallback(
    async (opts?: LoginOptions) => {
      await loginWithRedirect(auth0LoginArgs(opts, audience, scope));
    },
    [loginWithRedirect],
  );

  const logout = useCallback(() => {
    // Fixed drive-by: the old UserControls read server-only APP_BASE_URL from
    // client code (always undefined). The client-exposed var actually works —
    // the URL must be in the Auth0 app's Allowed Logout URLs (verified in
    // Task 3 before this ships).
    const returnTo = process.env.NEXT_PUBLIC_APP_BASE_URL;
    if (returnTo) auth0Logout({ logoutParams: { returnTo } });
    else auth0Logout();
  }, [auth0Logout]);

  const getAccessToken = useCallback(
    (opts?: GetTokenOptions) =>
      getAccessTokenSilently(auth0TokenArgs(opts, audience, scope)),
    [getAccessTokenSilently],
  );

  return { isLoading, isAuthenticated, error, user, login, logout, getAccessToken };
}

export const withAuthRequired = withAuthenticationRequired;
